import React from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/PromotionCard.css';

const PromotionCard = ({ 
  promotion, 
  isEditing, 
  editingPromo,
  onEdit, 
  onDelete, 
  onUpdate, 
  onCancel, 
  onChange,
  loading
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  
  // Format date according to locale
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(i18n.language);
    } catch (e) {
      return dateString;
    }
  };
  
  // Check if the promotion has ended
  const hasEnded = () => {
    if (!editingPromo.endDate) return false;
    
    const endDate = new Date(editingPromo.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for proper comparison
    
    return endDate < today;
  };
  
  // If this card is being edited, show edit form
  if (isEditing) {
    const isExpired = hasEnded();
    
    return (
      <div className="promotion-card">
        <div className="reward-edit-form">
          <div className="form-group">
            <label className="form-label">{t('rewards.promotions.form.name')}</label>
            <input 
              type="text" 
              name="name" 
              value={editingPromo.name}
              onChange={onChange}
              className="form-input"
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('rewards.promotions.form.multiplier')}</label>
            <input 
              type="number" 
              step="0.1"
              min="1"
              name="multiplier" 
              value={editingPromo.multiplier}
              onChange={onChange}
              className="form-input"
              dir="rtl" // Always left-to-right for numbers
            />
          </div>
          <div className="form-row">
            <div className="form-group half">
              <label className="form-label">{t('rewards.promotions.form.startDate')}</label>
              <input 
                type="date" 
                name="startDate" 
                value={editingPromo.startDate}
                onChange={onChange}
                className="form-input"
                dir="rtl" // Always left-to-right for dates
              />
            </div>
            <div className="form-group half">
              <label className="form-label">{t('rewards.promotions.form.endDate')}</label>
              <input 
                type="date" 
                name="endDate" 
                value={editingPromo.endDate}
                onChange={onChange}
                className="form-input"
                dir="rtl" // Always left-to-right for dates
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('rewards.promotions.form.description')}</label>
            <textarea 
              name="description" 
              value={editingPromo.description}
              onChange={onChange}
              className="form-textarea"
              rows="3"
              dir={isRTL ? 'rtl' : 'ltr'}
            ></textarea>
          </div>
          <div className="form-group checkbox">
            <label className={`form-checkbox-label ${isExpired ? 'disabled' : ''}`}>
              <input 
                type="checkbox" 
                name="active" 
                checked={editingPromo.active}
                onChange={onChange}
                disabled={isExpired}
              />
              {t('rewards.promotions.form.active')}
              {isExpired && (
                <span className="expired-note">
                  {t('rewards.promotions.form.expiredNote') || 'Cannot modify status of expired promotions'}
                </span>
              )}
            </label>
          </div>
          
          <div className="form-actions">
            <button 
              onClick={onUpdate} 
              className="primary-button"
              disabled={loading}
            >
              {loading ? t('common.updating') : t('rewards.promotions.form.buttons.update')}
            </button>
            <button 
              onClick={onCancel} 
              className="secondary-button"
              disabled={loading}
            >
              {t('rewards.promotions.form.buttons.cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Otherwise, show regular display
  return (
    <div className="promotion-card">
      <div className="reward-header">
        <div className="reward-title-row">
          <h4 className="reward-title">{promotion.name}</h4>
        </div>
        <div className="reward-points">
          {promotion.multiplier}× {t('rewards.promotions.multiplier')}
        </div>
      </div>
      
      <div className="promotion-dates">
        <div className="date-label">{t('rewards.promotions.dates.from')}</div>
        <div className="date-value">{formatDate(promotion.startDate)}</div>
        <div className="date-label">{t('rewards.promotions.dates.to')}</div>
        <div className="date-value">{formatDate(promotion.endDate)}</div>
      </div>
      
      <div className="reward-description">
        {promotion.description || t('rewards.promotions.form.noDescription')}
      </div>
      
      <div className="reward-actions">
        <span className={`status-indicator ${promotion.active ? 'active' : 'inactive'}`}>
          {promotion.active ? t('common.status.active') : t('common.status.inactive')}
        </span>
        <div className="action-buttons">
          <button 
            onClick={() => onEdit(promotion)} 
            className="edit-button"
          >
            {t('rewards.promotions.form.buttons.edit')}
          </button>
          <button 
            onClick={() => onDelete(promotion.id)} 
            className="delete-button"
          >
            {t('rewards.promotions.form.buttons.delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromotionCard;